import { useMemo, useState } from 'react';
import Modal from '../components/Modal.jsx';
import { useConfirm } from '../components/ConfirmDialog.jsx';
import { fmtCents, fmtDateShort, parseAmountToCents, todayIso } from '../format.js';
import { groupPairs } from '../txn-utils.js';

const TYPES = ['Expense', 'Income', 'Savings', 'Transfer', 'Debt Payment'];
const API_BASE = 'https://babb-finance-api.bryan-babb1.workers.dev';

/**
 * (groupPairs lives in ../txn-utils.js so Dashboard can use it too.)
 */

export default function Transactions({ state, lookups, mutate, reload, showToast }) {
  const [query, setQuery] = useState('');
  const [filterCat, setFilterCat] = useState('');
  const [editing, setEditing] = useState(null);  // 'new' or a row

  const filtered = useMemo(() => {
    let rows = state.transactions;
    if (query) {
      const q = query.toLowerCase();
      rows = rows.filter(t =>
        t.description.toLowerCase().includes(q) ||
        lookups.catName(t.category_id).toLowerCase().includes(q) ||
        lookups.acctName(t.account_id).toLowerCase().includes(q)
      );
    }
    if (filterCat) rows = rows.filter(t => t.category_id === filterCat);
    return rows;
  }, [state.transactions, query, filterCat, lookups]);

  // Collapse paired transfer rows into a single visual entry.
  // The displayed row carries: { ...debitLeg, _pair: { from, to, debit, credit } }
  // so the editor still gets a real transaction to open.
  const visualRows = useMemo(() => groupPairs(filtered), [filtered]);

  return (
    <div className="screen">
      <div className="row between" style={{ marginBottom: 16 }}>
        <h1 style={{ margin: 0 }}>Transactions</h1>
        <button className="primary" onClick={() => setEditing('new')}>+ Add</button>
      </div>

      <div className="row" style={{ marginBottom: 16, gap: 8 }}>
        <input
          placeholder="Search…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        <select value={filterCat} onChange={(e) => setFilterCat(e.target.value)} style={{ flex: '0 0 auto', maxWidth: 140 }}>
          <option value="">All categories</option>
          {state.categories.filter(c => !c.archived).map(c => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </select>
      </div>

      <div className="card" style={{ padding: 0 }}>
        {visualRows.length === 0 ? (
          <div className="empty">
            <div className="icon">∅</div>
            <div>{state.transactions.length === 0 ? 'No transactions yet' : 'No matches'}</div>
          </div>
        ) : (
          visualRows.map(t => (
            <button key={t.id} onClick={() => setEditing(t)} className="list-item"
                    style={{
                      width: '100%', textAlign: 'left', background: 'transparent',
                      border: 'none', borderBottom: '1px solid var(--border2)',
                      borderRadius: 0, cursor: 'pointer',
                    }}>
              <div className="grow">
                <div>
                  {t.description}
                  {t._pair && <span className="pill info" style={{ marginLeft: 8 }}>Transfer</span>}
                  {t.transfer_pair_id && !t._pair && <span className="pill warn" style={{ marginLeft: 8 }}>½ Transfer</span>}
                </div>
                <div className="meta">
                  {fmtDateShort(t.occurred_on)} · {lookups.catName(t.category_id)} ·{' '}
                  {t._pair
                    ? <>{lookups.acctName(t._pair.from_account_id)} → {lookups.acctName(t._pair.to_account_id)}</>
                    : lookups.acctName(t.account_id)}
                </div>
              </div>
              <span className={'amount ' + (t.amount_cents >= 0 ? 'pos' : 'neg')}>
                {fmtCents(t._pair ? -Math.abs(t._pair.debit.amount_cents) : t.amount_cents)}
              </span>
            </button>
          ))
        )}
      </div>

      <TransactionEditor
        editing={editing}
        onClose={() => setEditing(null)}
        state={state}
        mutate={mutate}
        reload={reload}
        showToast={showToast}
      />
    </div>
  );
}

function TransactionEditor({ editing, onClose, state, mutate, reload, showToast }) {
  if (!editing) return null;
  const isNew = editing === 'new';
  const initial = isNew ? {
    occurred_on: todayIso(),
    description: '',
    category_id: '',
    type: 'Expense',
    account_id: state.accounts.find(a => a.name === 'Checking')?.id ?? '',
    amount_cents: 0,
    notes: '',
  } : editing;

  return (
    <Modal open={!!editing} onClose={onClose}
           title={isNew ? 'Add Transaction' : 'Edit Transaction'}>
      <TransactionForm
        key={isNew ? 'new' : editing.id}
        initial={initial}
        isNew={isNew}
        state={state}
        mutate={mutate}
        reload={reload}
        showToast={showToast}
        onClose={onClose}
      />
    </Modal>
  );
}

function TransactionForm({ initial, isNew, state, mutate, reload, showToast, onClose }) {
  const ask = useConfirm();
  const [form, setForm] = useState({
    occurred_on: initial.occurred_on ?? todayIso(),
    description: initial.description ?? '',
    category_id: initial.category_id ?? '',
    type: initial.type ?? 'Expense',
    account_id: initial.account_id ?? '',
    destination_account_id: '',
    amountInput: initial.amount_cents ? (Math.abs(initial.amount_cents) / 100).toFixed(2) : '',
    notes: initial.notes ?? '',
  });
  const [saving, setSaving] = useState(false);
  const update = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const isTransferType = form.type === 'Savings' || form.type === 'Transfer';
  const isExistingTransfer = !isNew && initial.transfer_pair_id;

  async function save() {
    if (!form.description || !form.category_id || !form.account_id) {
      showToast({ kind: 'error', message: 'Description, category, and account are required.' });
      return;
    }
    let cents = parseAmountToCents(form.amountInput);
    if (cents == null) return showToast({ kind: 'error', message: 'Amount is invalid.' });

    setSaving(true);
    try {
      if (isNew && isTransferType) {
        if (!form.destination_account_id) {
          showToast({ kind: 'error', message: 'Pick a destination account.' });
          setSaving(false); return;
        }
        if (form.destination_account_id === form.account_id) {
          showToast({ kind: 'error', message: 'Destination must differ from source.' });
          setSaving(false); return;
        }
        const r = await fetch(API_BASE + '/transfers', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            occurred_on: form.occurred_on,
            description: form.description.trim(),
            category_id: form.category_id,
            type: form.type,
            from_account_id: form.account_id,
            to_account_id: form.destination_account_id,
            amount_cents: Math.abs(cents),
            notes: form.notes,
          }),
        });
        if (!r.ok) {
          const err = await r.json().catch(() => ({}));
          throw new Error(err.error || ('HTTP ' + r.status));
        }
        await reload();
        showToast({ kind: 'success', message: 'Transfer recorded.' });
      } else if (isNew) {
        if (form.type === 'Income') cents = Math.abs(cents);
        else cents = -Math.abs(cents);

        await mutate.addTransaction({
          occurred_on: form.occurred_on,
          description: form.description.trim(),
          category_id: form.category_id,
          type: form.type,
          account_id: form.account_id,
          amount_cents: cents,
          notes: form.notes,
        });
        showToast({ kind: 'success', message: 'Transaction added.' });
      } else {
        // Edit: keep sign aligned with existing sign
        const signedCents = initial.amount_cents >= 0 ? Math.abs(cents) : -Math.abs(cents);
        await mutate.updateTransaction(initial.id, {
          occurred_on: form.occurred_on,
          description: form.description.trim(),
          category_id: form.category_id,
          type: form.type,
          account_id: form.account_id,
          amount_cents: signedCents,
          notes: form.notes,
        });
        showToast({ kind: 'success', message: 'Saved.' });
      }
      onClose();
    } catch (e) {
      showToast({ kind: 'error', message: 'Failed: ' + e.message });
    } finally { setSaving(false); }
  }

  async function remove() {
    const isTransfer = !!initial.transfer_pair_id;
    const ok = await ask({
      title: isTransfer ? 'Delete transfer?' : 'Delete transaction?',
      message: isTransfer
        ? 'Both legs (debit + credit) will be removed. This cannot be undone.'
        : 'This cannot be undone.',
      confirmLabel: 'Delete',
      danger: true,
    });
    if (!ok) return;
    setSaving(true);
    try {
      await mutate.deleteTransaction(initial.id);
      if (isTransfer) await reload();
      showToast({ kind: 'success', message: 'Deleted.' });
      onClose();
    } catch (e) {
      showToast({ kind: 'error', message: 'Failed: ' + e.message });
    } finally { setSaving(false); }
  }

  return (
    <>
      {isExistingTransfer && (
        <div className="card" style={{
          background: 'rgba(37,99,235,0.10)',
          borderColor: 'rgba(37,99,235,0.35)',
          marginBottom: 16, padding: 12,
        }}>
          <div className="tiny muted" style={{ marginBottom: 4 }}>This is one leg of a transfer.</div>
          <div className="tiny">Editing changes this leg only. Delete removes both legs.</div>
        </div>
      )}

      <div className="field">
        <label>Date</label>
        <input type="date" value={form.occurred_on} onChange={(e) => update('occurred_on', e.target.value)} />
      </div>
      <div className="field">
        <label>Description</label>
        <input value={form.description} onChange={(e) => update('description', e.target.value)}
               placeholder="e.g. Sonic" autoFocus />
      </div>
      <div className="row" style={{ gap: 8 }}>
        <div className="field" style={{ flex: 1 }}>
          <label>Type</label>
          <select value={form.type} onChange={(e) => update('type', e.target.value)} disabled={!isNew}>
            {TYPES.map(t => <option key={t}>{t}</option>)}
          </select>
        </div>
        <div className="field" style={{ flex: 1 }}>
          <label>Amount</label>
          <input inputMode="decimal" placeholder="0.00"
                 value={form.amountInput}
                 onChange={(e) => update('amountInput', e.target.value)} />
        </div>
      </div>
      <div className="field">
        <label>Category</label>
        <select value={form.category_id} onChange={(e) => update('category_id', e.target.value)}>
          <option value="">Select…</option>
          {state.categories.filter(c => !c.archived).map(c => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </select>
      </div>
      <div className="field">
        <label>{isNew && isTransferType ? 'From Account' : 'Account'}</label>
        <select value={form.account_id} onChange={(e) => update('account_id', e.target.value)}>
          <option value="">Select…</option>
          {state.accounts.filter(a => !a.archived).map(a => (
            <option key={a.id} value={a.id}>{a.name}</option>
          ))}
        </select>
      </div>
      {isNew && isTransferType && (
        <div className="field">
          <label>To Account</label>
          <select value={form.destination_account_id}
                  onChange={(e) => update('destination_account_id', e.target.value)}>
            <option value="">Select destination…</option>
            {state.accounts.filter(a => !a.archived && a.id !== form.account_id).map(a => (
              <option key={a.id} value={a.id}>{a.name}</option>
            ))}
          </select>
        </div>
      )}
      <div className="field">
        <label>Notes</label>
        <input value={form.notes} onChange={(e) => update('notes', e.target.value)} />
      </div>

      <div className="row between" style={{ marginTop: 24 }}>
        {!isNew ? (
          <button className="danger" onClick={remove} disabled={saving}>Delete</button>
        ) : <span />}
        <div className="row" style={{ gap: 8 }}>
          <button onClick={onClose}>Cancel</button>
          <button className="primary" onClick={save} disabled={saving}>
            {saving ? <span className="spinner" /> : 'Save'}
          </button>
        </div>
      </div>
    </>
  );
}
